import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { htmlToStructuredText, countWords } from "@context/shared";
import { sanitizeDraftHtmlForDom } from "../../lib/sanitizeHtml";
import type { MutationSource } from "../../state/studentReducer";

export interface WritingEditorHandle {
  focus(): void;
  /** Runs a formatting command and reports the resulting content. */
  applyCommand(command: string, value?: string): void;
}

export interface WritingEditorProps {
  html: string;
  /** Bumped by the provider whenever the DOM must be rewritten from state. */
  contentVersion: number;
  trackingActive: boolean;
  onMutation: (input: {
    html: string;
    text: string;
    source: MutationSource;
    paragraphCount: number;
  }) => void;
  onKeydown: (key: string) => void;
  onPaste: (characters: number, words: number) => void;
  onFocus: () => void;
  onBlur: () => void;
}

/**
 * The contenteditable writing surface.
 *
 * React never re-renders its children: the DOM is written imperatively only
 * when `contentVersion` changes (load, restore, reset, submit), so typing never
 * loses the caret. Every DOM event is forwarded to the reducer, which owns all
 * counting.
 */
export const WritingEditor = forwardRef<WritingEditorHandle, WritingEditorProps>(
  function WritingEditor(props, ref) {
    const { html, contentVersion, trackingActive, onMutation, onKeydown, onPaste, onFocus, onBlur } =
      props;
    const editorRef = useRef<HTMLDivElement>(null);
    const appliedVersion = useRef(-1);
    // Set while `execCommand` runs inside onPaste. The browser dispatches a
    // synchronous `input` event for the insert, so the onInput handler must not
    // emit a second mutation on top of the paste's own insert.
    const pasteInProgress = useRef(false);
    // Set while `applyCommand` runs. execCommand fires a synchronous `input`
    // event in mainstream browsers, so the onInput handler owns that mutation
    // and clears the flag; applyCommand emits "formatting" only when the
    // browser did not fire input (jsdom's stub, old Edge, ...).
    const formattingInProgress = useRef(false);

    useEffect(() => {
      const node = editorRef.current;
      if (!node || appliedVersion.current === contentVersion) return;
      appliedVersion.current = contentVersion;
      // The only `innerHTML` assignment in the app, and the stored markup it
      // writes came from a client, so it is rebuilt through the allowlist first.
      node.innerHTML = sanitizeDraftHtmlForDom(html);
    }, [html, contentVersion]);

    const emitMutation = (source: MutationSource) => {
      const node = editorRef.current;
      if (!node) return;
      const html = node.innerHTML;
      onMutation({
        html,
        // The markup, not the rendered text, is the source of truth for
        // structure: block boundaries inside innerHTML are what let the PDF
        // and email render real paragraph breaks. Deriving from innerText
        // collapses every newline to a space and fuses the whole writing
        // space into one continuous paragraph.
        text: htmlToStructuredText(html),
        source,
        paragraphCount: node.querySelectorAll("p").length,
      });
    };

    useImperativeHandle(ref, () => ({
      focus() {
        editorRef.current?.focus();
      },
      applyCommand(command, value) {
        const node = editorRef.current;
        if (!node) return;
        node.focus();
        // `execCommand` is deprecated but is what the prototype's toolbar used;
        // keeping it preserves the exact formatting behaviour. It fires a
        // synchronous `input` event in mainstream browsers, so the onInput
        // handler consumes the flag and emits the (single) mutation. Only when
        // the browser does not fire input does this emit "formatting" itself.
        // Wrapped in try/finally (mirroring the paste path) so a throwing or
        // unsupported command (which reports false) cannot leave the flag set
        // and misroute the next input event — and, like paste, must not emit a
        // phantom mutation for a command that changed nothing.
        formattingInProgress.current = true;
        let applied = false;
        try {
          applied = document.execCommand(command, false, value);
        } finally {
          if (formattingInProgress.current) {
            formattingInProgress.current = false;
            if (applied) emitMutation("formatting");
          }
        }
      },
    }));

    return (
      <article
        ref={editorRef}
        className="editor-surface"
        contentEditable={trackingActive}
        suppressContentEditableWarning
        aria-label="Student writing editor"
        role="textbox"
        aria-multiline="true"
        onInput={() => {
          // A paste's `execCommand` insert fires a real `input` event in every
          // mainstream browser. That event is the paste's mutation: emit it and
          // consume the flag so the paste handler's fallback cannot add a second
          // (zero-delta) mutation into the session record.
          if (pasteInProgress.current) {
            pasteInProgress.current = false;
            emitMutation("input");
            return;
          }
          // The same for a toolbar formatting command: the browser's input
          // event is the mutation, so consume the flag and emit it here rather
          // than letting applyCommand emit a second "formatting" mutation.
          if (formattingInProgress.current) {
            formattingInProgress.current = false;
            emitMutation("input");
            return;
          }
          emitMutation("input");
        }}
        onKeyDown={(event) => {
          if (!trackingActive) {
            event.preventDefault();
            return;
          }
          onKeydown(event.key);
        }}
        onPaste={(event) => {
          if (!trackingActive) return;
          // The browser's default paste would drop the raw clipboard HTML into
          // the contenteditable and execute anything it carries (e.g. copied
          // <img onerror=...>). Cancel it and insert a sanitized fragment at the
          // caret instead.
          event.preventDefault();
          const plainText = event.clipboardData?.getData("text/plain") ?? "";
          onPaste(plainText.length, countWords(plainText));

          pasteInProgress.current = true;
          const html = event.clipboardData?.getData("text/html");
          const cleanHtml = html ? sanitizeDraftHtmlForDom(html) : "";
          let applied = false;
          try {
            // `execCommand` is deprecated, but like the toolbar it is the only
            // broadly supported way to write HTML at the caret in a contenteditable.
            if (html) {
              applied = document.execCommand("insertHTML", false, cleanHtml);
            } else {
              // No markup on the clipboard: plain text is inert, insert it verbatim.
              applied = document.execCommand("insertText", false, plainText);
            }
          } finally {
            // If the command threw or reported failure (unsupported command),
            // nothing was inserted: clear the flag so a later real input is not
            // swallowed, and skip the fallback — the normal input path takes over.
            if (!applied) pasteInProgress.current = false;
          }
          // An empty clipboard (insertText("")) or a clipboard whose HTML the
          // allowlist strips to "" (an image or <input>/<script>-only copy)
          // inserts nothing, so there is no DOM change and no input event. The
          // gate must check the branch actually taken: a non-empty text/plain
          // rider on an image copy must not schedule a fallback that flushes a
          // phantom zero-delta mutation. The plain-text gate also trims to
          // non-whitespace: a whitespace-only clipboard passes
          // `plainText.length > 0` but would produce a delta-0 text_input, so
          // `trim()` gates that phantom out too.
          if (applied && (html ? cleanHtml.length > 0 : plainText.trim().length > 0)) {
            // `execCommand` fires a synchronous `input` event in mainstream
            // browsers, which the onInput handler above already routed through the
            // pipeline (and cleared the flag). For a browser that does not fire
            // input, flush the inserted content once the event loop settles.
            setTimeout(() => {
              if (pasteInProgress.current) {
                pasteInProgress.current = false;
                emitMutation("input");
              }
            }, 0);
          } else {
            // Applied but with nothing to flush (or the command failed): no
            // fallback was scheduled, so clear the flag here too. A stale flag
            // would otherwise be consumed by the NEXT input event and turn a
            // single real mutation into two.
            pasteInProgress.current = false;
          }
        }}
        onFocus={onFocus}
        onBlur={onBlur}
      />
    );
  },
);
